import { MilestoneToBeBilled } from '@/services/billing/types/billing.type';
import moment from 'moment';
import { LegalEntityDistribution } from '../store/billing.store';
import { CreateSalesOrderRequest } from '@/services/billing/types/create-billing.type';
import { ChoicesOrderItemTypeForBilling, ChoicesOrderUnitMeasure } from '../../orders/types';
import { IOrderDocument } from '@/types/order-document';

export interface GroupedUniqueItem {
  subscription: string;
  type: string;
  productName: string;
  plan: {
    name: string;
    code: string;
  };
  partnerLogo: string;
  provisionDate: string;
  renewalDate: string;
  sku: string;
  quantity: number;
  totalAmount: number;
  milestones: MilestoneToBeBilled[];
}

export const groupUniqueItems = (selectedMilestones: MilestoneToBeBilled[]) => {
  const uniqueItems = new Map<string, GroupedUniqueItem>();

  selectedMilestones.forEach((milestone) => {
    const orderType = milestone.orderItem.orderItemType || '';
    const subscription = milestone.orderItem.subscription;
    const key = `${subscription}-${orderType}`;
    const existingItem = uniqueItems.get(key);

    if (!existingItem) {
      const milestones = selectedMilestones
        .sort((a, b) => a.milestonePeriod - b.milestonePeriod)
        .filter(
          (s) =>
            s.orderItem.orderItemType === orderType && s.orderItem.subscription === subscription
        );
      let totalAmount = 0;

      milestones.forEach((m) => {
        totalAmount += m.total;
      });
      uniqueItems.set(key, {
        subscription: milestone.orderItem.subscription,
        type: milestone.orderItem.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud,
        productName: milestone.product.name,
        plan: {
          name: milestone.plan.name,
          code: milestone.plan.code,
        },
        partnerLogo: milestone.partner.logo.url,
        sku: milestone.sku.name,
        provisionDate: moment(milestone.orderItem.provisionDate).utc().format('L'),
        renewalDate: moment(milestone.orderItem.renewalDateCommercial).utc().format('L'),
        quantity: milestone.orderItem.quantity,
        totalAmount,
        milestones,
      });
    }
  });

  return Array.from(uniqueItems.values());
};

export interface DistributionListItem {
  legalEntity: {
    _id: string;
    name: string;
    taxNumber: string;
  };
  items: GroupedUniqueItem[];
  documents?: IOrderDocument[];
}

export const buildDistribuitionListData = (distribution: LegalEntityDistribution[]) => {
  const distribuitionItems: DistributionListItem[] = [];

  distribution.forEach((distributionItem) => {
    const items: GroupedUniqueItem[] = [];
    distributionItem.subscriptionItems.forEach(({ milestones }) => {
      const data = groupUniqueItems(milestones);
      items.push(...data);
    });

    distribuitionItems.push({
      legalEntity: {
        _id: distributionItem.legalEntity._id,
        name: distributionItem.legalEntity.legal_name,
        taxNumber: distributionItem.legalEntity.tax_number,
      },
      items,
    });
  });

  return distribuitionItems;
};

export const buildThirdStepDistribuitionListDataFromSalesOrders = (
  salesOrders: CreateSalesOrderRequest[],
  legalEntityDistributions: LegalEntityDistribution[],
  allMilestones: MilestoneToBeBilled[],
  secondStepFormValues?: { legalEntity: { _id: string }; documents?: IOrderDocument[] }[]
): DistributionListItem[] => {
  const distribuitionItems: DistributionListItem[] = [];

  salesOrders.forEach((salesOrder) => {
    // Find the corresponding legal entity distribution to get details
    const legalEntityDistribution = legalEntityDistributions.find(
      (led) => led.legalEntity._id === salesOrder._id
    );

    if (!legalEntityDistribution) return;

    const items: GroupedUniqueItem[] = [];

    salesOrder.items.forEach((item) => {
      // Find the milestone for this item to get product/plan details
      // Note: An item might correspond to multiple milestones, but for display
      // we usually group by plan/product which comes from the milestone.
      // Taking the first milestone is a simplification that works if items are grouped by plan/product.
      const firstMilestoneId = item.milestones[0];
      const milestone = allMilestones.find((m) => m._id === firstMilestoneId);

      if (milestone) {
        // We reconstruct the BillingOrderSummaryItem but with the CALCULATED amount
        items.push({
          subscription: item.subscription, // Using subscription ID as item ID for grouping
          type: milestone.orderItem.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud,
          productName: milestone.product.name,
          plan: {
            name: milestone.plan.name,
            code: milestone.plan.code,
          },
          partnerLogo: milestone.partner.logo.url,
          sku: milestone.sku.name,
          provisionDate: moment(milestone.orderItem.provisionDate).utc().format('L'),
          renewalDate: moment(milestone.orderItem.renewalDateCommercial).utc().format('L'),
          quantity: milestone.orderItem.quantity,
          totalAmount: item.amount, // USE THE CALCULATED AMOUNT
          milestones: allMilestones.filter((m) => item.milestones.includes(m._id)),
        });
      }
    });

    // Get documents from secondStepFormValues for this legal entity
    const formValueForLegalEntity = secondStepFormValues?.find(
      (fv) => fv.legalEntity._id === salesOrder._id
    );
    const documents = formValueForLegalEntity?.documents || [];

    distribuitionItems.push({
      legalEntity: {
        _id: legalEntityDistribution.legalEntity._id,
        name: legalEntityDistribution.legalEntity.legal_name,
        taxNumber: legalEntityDistribution.legalEntity.tax_number,
      },
      items,
      documents,
    });
  });

  return distribuitionItems;
};

export const groupUniqueUnitMeasureDistribution = (groupedUniqueItems: GroupedUniqueItem[]) => {
  const unitMeasureDistribution = new Map<string, ChoicesOrderUnitMeasure>();
  groupedUniqueItems.forEach((item: GroupedUniqueItem) => {
    const orderType = item.type;
    const subscription = item.subscription;
    const key = `${subscription}-${orderType}`;
    const existingItem = unitMeasureDistribution.get(key);
    if (!existingItem) {
      unitMeasureDistribution.set(key, ChoicesOrderUnitMeasure.amount);
    }
  });

  return Object.fromEntries(unitMeasureDistribution);
};

/**
 * Groups unique items using amount information from salesOrders.
 * This aggregates amounts across all legal entities for each subscription/type combination.
 * The totalAmount includes the calculated amounts after discount distribution.
 */
export const groupUniqueItemsFromSalesOrders = (
  salesOrders: CreateSalesOrderRequest[],
  selectedMilestones: MilestoneToBeBilled[]
): GroupedUniqueItem[] => {
  // Map to aggregate amounts by subscription-orderType key
  const aggregatedAmounts = new Map<string, number>();

  // Aggregate amounts from all salesOrders items
  salesOrders.forEach((salesOrder) => {
    salesOrder.items.forEach((item) => {
      // Find a milestone to get the orderType
      const milestone = selectedMilestones.find((m) => item.milestones.includes(m._id));
      if (!milestone) return;

      const orderType = milestone.orderItem.orderItemType || '';
      const key = `${item.subscription}-${orderType}`;

      const currentAmount = aggregatedAmounts.get(key) || 0;
      // Use subAmount (amount after discount) if available, otherwise use amount
      aggregatedAmounts.set(key, currentAmount + (item.subAmount ?? item.amount));
    });
  });

  // Now create the GroupedUniqueItem array using the aggregated amounts
  const uniqueItems = new Map<string, GroupedUniqueItem>();

  selectedMilestones.forEach((milestone) => {
    const orderType = milestone.orderItem.orderItemType || '';
    const subscription = milestone.orderItem.subscription;
    const key = `${subscription}-${orderType}`;
    const existingItem = uniqueItems.get(key);

    if (!existingItem) {
      const milestones = selectedMilestones
        .sort((a, b) => a.milestonePeriod - b.milestonePeriod)
        .filter(
          (s) =>
            s.orderItem.orderItemType === orderType && s.orderItem.subscription === subscription
        );

      // Get the aggregated amount from salesOrders, fallback to milestone sum
      const aggregatedAmount = aggregatedAmounts.get(key);
      const totalAmount = aggregatedAmount ?? milestones.reduce((acc, m) => acc + m.total, 0);

      uniqueItems.set(key, {
        subscription: milestone.orderItem.subscription,
        type: milestone.orderItem.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud,
        productName: milestone.product.name,
        plan: {
          name: milestone.plan.name,
          code: milestone.plan.code,
        },
        partnerLogo: milestone.partner.logo.url,
        sku: milestone.sku.name,
        provisionDate: moment(milestone.orderItem.provisionDate).utc().format('L'),
        renewalDate: moment(milestone.orderItem.renewalDateCommercial).utc().format('L'),
        quantity: milestone.orderItem.quantity,
        totalAmount,
        milestones,
      });
    }
  });

  return Array.from(uniqueItems.values());
};
